import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import Link from 'carbon-react/lib/components/link';
import Typography from 'carbon-react/lib/components/typography';

import excelLogo from '@futrli/shared-services/excel-upload/excel-modals/update/img/Excel Logo.png';
import styles from './excel-modal-content.scss';

interface Props {
  forecastType: 'budget' | 'scenario';
  forecastTemplateLink: string;
}

export const CreateFromExcelModalContent = ({ forecastType, forecastTemplateLink }: Props) => {
  const intl = useIntl();
  // Carbon forwards native anchor attributes, although ButtonProps does not declare `download`.
  const downloadButtonProps = {
    download: intl.formatMessage({
      id: 'predict.forecast_template_filename',
      defaultMessage: 'Forecast Template.xls'
    })
  };

  return (
    <Box className={styles.container}>
      <img src={excelLogo} alt="Excel" width={48} height={48} style={{ marginBottom: '16px' }} />
      <Typography fontSize="14px" fontWeight="500" mb={0}>
        <FormattedMessage id="predict.create_forecast.step_x" defaultMessage="Step {step}" values={{ step: 1 }} />
      </Typography>
      <Typography fontSize="14px" mb="8px" color="var(--colors-utility-yin-055, rgba(0, 0, 0, 0.55))">
        <FormattedMessage
          id="predict.download_excel_template_below"
          defaultMessage="Download the Excel template below."
        />
      </Typography>

      <Button
        buttonType="secondary"
        mb="16px"
        iconType="download"
        iconPosition="after"
        href={forecastTemplateLink}
        {...downloadButtonProps}
      >
        {forecastType === 'budget' ? (
          <FormattedMessage id="predict.1_year_p_and_l_template" defaultMessage="1-year P&L template.xls" />
        ) : (
          <FormattedMessage id="predict.3_year_cash_flow_template" defaultMessage="3-year cash flow template.xls" />
        )}
      </Button>

      <Typography fontSize="14px" fontWeight="500" mb={0}>
        <FormattedMessage id="predict.create_forecast.step_x" defaultMessage="Step {step}" values={{ step: 2 }} />
      </Typography>
      <Typography fontSize="14px" color="var(--colors-utility-yin-055, rgba(0, 0, 0, 0.55))" mb="16px">
        {forecastType === 'budget' ? (
          <>
            <FormattedMessage
              id="predict.budget_data_enter"
              defaultMessage="Enter P&L data, following the <link>import guidelines</link>."
              values={{
                link: chunks => (
                  <Link
                    href="https://support.futrli.com/en/articles/11631222-how-to-create-a-forecast-with-an-excel-template"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span style={{ fontWeight: 500 }}>{chunks}</span>
                  </Link>
                )
              }}
            />{' '}
            <FormattedMessage
              id="predict.figure_entered_monthly_budget_note"
              defaultMessage="Note: figures entered must be monthly."
            />
          </>
        ) : (
          <>
            <FormattedMessage
              id="predict.scenario_p_and_l_data_enter"
              defaultMessage="Enter P&L data, following the <link>import guidelines</link>."
              values={{
                link: chunks => (
                  <Link
                    href="https://support.futrli.com/en/articles/11631222-how-to-create-a-forecast-with-an-excel-template"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span style={{ fontWeight: 500 }}>{chunks}</span>
                  </Link>
                )
              }}
            />{' '}
            <FormattedMessage
              id="predict.figure_enter_monthly_scenario_node"
              defaultMessage="Note: figures entered must be monthly and cash flow settings must be entered into the file."
            />
          </>
        )}
      </Typography>

      <Typography fontSize="14px" fontWeight="500" mb={0}>
        <FormattedMessage id="predict.create_forecast.step_x" defaultMessage="Step {step}" values={{ step: 3 }} />
      </Typography>
      <Typography fontSize="14px" mb={0} color="var(--colors-utility-yin-055, rgba(0, 0, 0, 0.55))">
        <FormattedMessage
          id="predict.upload_data_file_max_size_25.step_3"
          defaultMessage="Upload the updated file. Max file size is 25mb."
        />
      </Typography>
    </Box>
  );
};
